<template>
    <div
        v-if="ready"
        class="container"
    >
        <Controls
            :undo="undo"
            :redo="redo"
            :rotate-left="rotateLeft"
            :rotate-right="rotateRight"
            :edit="edit"
            :crop="crop"
            :stack-pos="stackPos"
            :stack="stack"
            :set-brightness="setBrightness"
            :set-contrast="setContrast"

            @save="save"
        />
        <CanvasWrapper
            :wrapper="wrapper"
        >
            <Cropper
                v-if="edit.cropping"
                :edit="edit"
            />

            <Canvas
                v-if="ready"
                :original-image="originalImage"
                :image="image"
                :scale="scale"
                :canvas="canvas"
                :ctx="ctx"
                :set-canvas="setCanvas"
                :set-ctx="setCtx"
                :push-stack="pushStack"
            />
        </CanvasWrapper>
    </div>
</template>

<script>
import { brightness, contrast } from '../filters';
import {
    Canvas, CanvasWrapper, Controls, Cropper
} from '.';

export default {
    name: 'Container',
    components: {
        CanvasWrapper,
        Canvas,
        Controls,
        Cropper
    },
    props: {
        src: {
            type: String,
            required: true
        },
        type: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            ready: false,

            wrapper: {
                width: 0,
                height: 0
            },
            image: {
                height: 0,
                width: 0
            },

            canvas: false,
            ctx: false,

            stackPos: -1,
            stack: [],

            edit: {
                cropping: false,
                brightness: false,
                contrast: false,
                cropDimensions: {
                    x0: 0,
                    x1: 0,
                    y0: 0,
                    y1: 0
                },
                slider: 0
            }
        };
    },
    computed: {
        originalImage() {
            if (!this.src) return false;
            const im = new Image();
            im.crossOrigin = 'Anonymous';
            im.src = this.src;
            this.setImageEventListener(im);
            return im;
        },
        scale() {
            let left = 0;
            let top = 0;
            const x = this.wrapper.width / this.image.width;
            const y = this.wrapper.height / this.image.height;
            const scale = Math.min(x, y);

            if (x > y) {
                left = ((this.wrapper.width - this.image.width * scale) / 2).toFixed(0);
            }
            else {
                top = ((this.wrapper.height - this.image.height * scale) / 2).toFixed(0);
            }

            return {
                scale,
                left,
                top
            };
        }
    },
    watch: {
        src() {
            this.ready = false;
            // eslint-disable-next-line no-unused-expressions
            this.originalImage;
        }
    },
    mounted() {
        // eslint-disable-next-line no-unused-expressions
        this.originalImage;
    },
    methods: {
        setImageEventListener(im) {
            im.addEventListener('load', () => {
                this.image.height = im.height;
                this.image.width = im.width;
                this.ready = true;
            });
        },
        setCanvas(canvas) {
            this.canvas = canvas;
        },
        setCtx(ctx) {
            this.ctx = ctx;
        },
        pushStack(src) {
            this.stack = this.stack.slice(0, this.stackPos + 1);
            this.stack.push({
                data: src.data,
                height: src.height,
                width: src.width
            });
            this.stackPos = this.stack.length - 1;
        },
        save() {
            this.canvas.toBlob(
                blob => this.$emit('save', blob),
                this.type,
                1
            );
        },

        undo() {
            if (this.stackPos <= 0) {
                return;
            }
            this.stackPos--;
            const src = this.stack[this.stackPos];
            const image = new ImageData(src.data, src.width, src.height);

            this.image = {
                width: src.width,
                height: src.height
            };

            this.$nextTick()
                .then(() => {
                    this.ctx.putImageData(image, 0, 0);
                });
        },
        redo() {
            if (this.stackPos >= this.stack.length - 1) {
                return;
            }
            this.stackPos++;
            const src = this.stack[this.stackPos];
            const image = new ImageData(src.data, src.width, src.height);

            this.image = {
                width: src.width,
                height: src.height
            };

            this.$nextTick()
                .then(() => {
                    this.ctx.putImageData(image, 0, 0);
                });
        },

        crop() {
            this.edit.cropping = false;
            const crop = this.edit.cropDimensions;
            const e = {
                x: (Math.min(crop.x0, crop.x1) - this.scale.left) / this.scale.scale,
                y: (Math.min(crop.y0, crop.y1) - this.scale.top) / this.scale.scale,
                width: Math.abs(crop.x0 - crop.x1) / this.scale.scale,
                height: Math.abs(crop.y0 - crop.y1) / this.scale.scale
            };

            const croppedImage = this.ctx.getImageData(e.x, e.y, e.width, e.height);

            this.image.width = e.width;
            this.image.height = e.height;

            this.$nextTick()
                .then(() => {
                    this.ctx.putImageData(croppedImage, 0, 0);
                    this.pushStack(croppedImage);
                });
        },

        rotateLeft() {
            this.rotate(-90);
        },
        rotateRight() {
            this.rotate(90);
        },

        rotate(input) {
            const degrees = (input * Math.PI) / 180;

            const tempCanvas = document.createElement('canvas');
            const tempCtx = tempCanvas.getContext('2d');
            tempCanvas.width = this.image.width;
            tempCanvas.height = this.image.height;
            // put our data onto the temp canvas
            tempCtx.drawImage(this.canvas, 0, 0, tempCanvas.width, tempCanvas.height);

            this.ctx.clearRect(0, 0, this.image.width, this.image.height);

            const { height } = this.image;
            this.image.height = this.image.width;
            this.image.width = height;

            this.$nextTick()
                .then(() => {
                    this.ctx.translate(this.image.width / 2, this.image.height / 2);
                    this.ctx.rotate(degrees);
                    this.ctx.drawImage(
                        tempCanvas,
                        -this.image.height / 2,
                        -this.image.width / 2,
                        this.image.height,
                        this.image.width
                    );

                    const rotatedImage = this.ctx.getImageData(
                        0,
                        0,
                        this.image.width,
                        this.image.height
                    );
                    this.pushStack(rotatedImage);
                });
        },
        setBrightness() {
            const data = this.ctx.getImageData(0, 0, this.image.width, this.image.height);
            const newImage = brightness(data, (this.edit.slider * 4));
            this.ctx.putImageData(newImage, 0, 0);
            this.pushStack(newImage);
        },

        setContrast() {
            const data = this.ctx.getImageData(0, 0, this.image.width, this.image.height);
            const newImage = contrast(data, (this.edit.slider * 4));
            this.ctx.putImageData(newImage, 0, 0);
            this.pushStack(newImage);
        }
    }
};
</script>

<style lang="scss" scoped>
.container {
    position: relative;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;
    max-width: 100%;
    padding: 0;
}
</style>
